// Dev-only ledger of Helius RPC calls. UNLIKE the realtime registry,
// which aggregates by topic, this stores **every individual call** in a
// bounded ring buffer so engineers can see exactly what was sent, when,
// from where, and — critically — whether it duplicates a recent call
// (same method + same params) or could be batched with a recent call
// (same method, different params, where a multi-variant exists).
//
// === Two classes of waste ===
//
// 1. DUPLICATE — same method + same params within ~1s.
//    Should have been cached / deduped. Worst-class waste; the second
//    call returns the same bytes as the first.
//
// 2. BATCHABLE — same method, DIFFERENT params, within ~1s, where the
//    Solana/Helius API has a multi-input variant of that method (e.g.
//    `getAccountInfo` → `getMultipleAccountsInfo`). Should be combined
//    into one round-trip; significantly cheaper on Helius credits.
//
// A single call can be flagged for BOTH (a duplicate burst is also a
// batchable burst, but the dupe label takes precedence in the UI).
//
// === Plumbing ===
//
// `makeInstrumentedHeliusFetch` (sibling file) intercepts the `fetch`
// argument the @solana/web3.js Connection uses for HTTP JSON-RPC and
// reports each call here.
//
// === Zero-overhead in production ===
//
// Every public function gates on `IS_DEV` and returns a constant no-op
// before touching state. State (the ring buffer, dedup maps, listener
// set) is lazily allocated on first dev-mode use via `ensureState()`.
// In a production bundle:
//
//   - Webpack/Turbopack constant-fold `process.env.NODE_ENV !== 'production'`
//     to `false`, dead-code-eliminating the IS_DEV branches.
//   - `connection.ts` calls `makeInstrumentedHeliusFetch()` only inside a
//     `process.env.NODE_ENV !== 'production'` ternary, so the call is
//     removed at build time and the instrumenter import becomes
//     unreferenced → tree-shaken out of the bundle.
//   - The DevToolkit (which imports this UI) only loads via `next/dynamic`
//     after the visibility gate passes, so the registry code only ships
//     in the lazy DevToolkit chunk for internal users.
//
// What remains in the prod bundle for non-internal users: nothing.

const IS_DEV: boolean = process.env.NODE_ENV !== 'production';

/** Max calls retained in the ring buffer. Beyond this, oldest is dropped. */
const MAX_BUFFER_SIZE = 500;
/** Window (ms) within which two calls are considered "in quick succession"
 *  — applies to both the duplicate detector and the batchable detector. */
const WINDOW_MS = 1000;

/**
 * Methods with a known multi-input variant on the Solana RPC or Helius
 * DAS API. When ≥2 distinct calls of one of these methods land within
 * `WINDOW_MS`, every member of the burst is flagged "BATCHABLE" with
 * the suggested replacement.
 *
 * Sources:
 *   - Solana RPC: getAccountInfo / getBalance / getTokenAccountBalance
 *     all map onto `getMultipleAccountsInfo` (the latter two with a
 *     lamports / token-account decoder).
 *   - Helius DAS: getAsset / getAssetProof have explicit ...Batch
 *     siblings that take up to 1000 ids per call.
 */
const BATCHABLE_METHODS: Readonly<Record<string, string>> = {
  getAccountInfo: 'getMultipleAccountsInfo',
  getBalance: 'getMultipleAccountsInfo + lamports decode',
  getTokenAccountBalance: 'getMultipleAccountsInfo + SPL token decode',
  getAsset: 'getAssetBatch',
  getAssetProof: 'getAssetProofBatch',
};

export interface HeliusCall {
  readonly id: number;
  readonly timestamp: number;
  readonly method: string;
  /** Truncated params (≤ 120 chars) for display. */
  readonly paramsPreview: string;
  /** Full stringified params used as the dedup key. */
  readonly paramsKey: string;
  readonly bytes: number;
  readonly callSite: string | null;
  /** When > 1, this call is one of N duplicates (same method+params)
   *  seen within the window. UI flags any value > 1. */
  readonly burstSize: number;
  /** Stable id grouping members of a duplicate burst (or null). */
  readonly burstId: number | null;
  /** When > 1, this call is one of N batchable siblings (same method,
   *  any params, within window) AND `method` is in the batchable list. */
  readonly batchSize: number;
  /** Stable id grouping members of a batchable burst (or null). */
  readonly batchId: number | null;
  /** Suggested replacement — only present when batchSize > 1. */
  readonly batchSuggestion: string | null;
  /** Full JSON-RPC response body (capped). Captured by the instrumenter
   *  after the fetch resolves. Null for failed reads. */
  readonly responseBody: string | null;
}

export interface HeliusSnapshot {
  readonly totalCalls: number;
  readonly totalBytes: number;
  readonly duplicateCalls: number;
  readonly batchableCalls: number;
  readonly startedAt: number;
  /** Latest call last; UI typically reverses to show newest at top. */
  readonly calls: readonly HeliusCall[];
}

interface MutableHeliusCall {
  id: number;
  timestamp: number;
  method: string;
  paramsPreview: string;
  paramsKey: string;
  bytes: number;
  callSite: string | null;
  burstSize: number;
  burstId: number | null;
  batchSize: number;
  batchId: number | null;
  batchSuggestion: string | null;
  responseBody: string | null;
}

interface RegistryState {
  buffer: MutableHeliusCall[];
  /** dedup-key (method::params) → recent timestamps + burst id assigned
   *  on the first occurrence. */
  recent: Map<string, { timestamps: number[]; burstId: number }>;
  /** method → recent timestamps. Used for the batchable detector. */
  methodRecent: Map<string, number[]>;
  listeners: Set<() => void>;
  startedAt: number;
  totalCalls: number;
  totalBytes: number;
  idSeq: number;
  burstSeq: number;
  batchSeq: number;
  cachedSnapshot: HeliusSnapshot;
}

interface HeliusGlobals {
  __morfiHeliusLedger?: RegistryState;
}

const NOOP_SNAPSHOT: HeliusSnapshot = Object.freeze({
  totalCalls: 0,
  totalBytes: 0,
  duplicateCalls: 0,
  batchableCalls: 0,
  startedAt: 0,
  calls: [],
});

const NOOP_UNSUBSCRIBE = (): void => {};

let _state: RegistryState | null = null;

function ensureState(): RegistryState {
  if (_state !== null) return _state;
  const g = globalThis as typeof globalThis & HeliusGlobals;
  const existing = g.__morfiHeliusLedger;
  if (existing !== undefined) {
    _state = existing;
    return existing;
  }
  const fresh: RegistryState = {
    buffer: [],
    recent: new Map(),
    methodRecent: new Map(),
    listeners: new Set(),
    startedAt: Date.now(),
    totalCalls: 0,
    totalBytes: 0,
    idSeq: 0,
    burstSeq: 0,
    batchSeq: 0,
    cachedSnapshot: NOOP_SNAPSHOT,
  };
  fresh.cachedSnapshot = freezeSnapshotFrom(fresh);
  g.__morfiHeliusLedger = fresh;
  _state = fresh;
  return fresh;
}

function freezeSnapshotFrom(s: RegistryState): HeliusSnapshot {
  let dupes = 0;
  let batchable = 0;
  const calls: HeliusCall[] = s.buffer.map((c) => {
    if (c.burstSize > 1) dupes += 1;
    if (c.batchSize > 1) batchable += 1;
    return {
      id: c.id,
      timestamp: c.timestamp,
      method: c.method,
      paramsPreview: c.paramsPreview,
      paramsKey: c.paramsKey,
      bytes: c.bytes,
      callSite: c.callSite,
      burstSize: c.burstSize,
      burstId: c.burstId,
      batchSize: c.batchSize,
      batchId: c.batchId,
      batchSuggestion: c.batchSuggestion,
      responseBody: c.responseBody,
    };
  });
  return {
    totalCalls: s.totalCalls,
    totalBytes: s.totalBytes,
    duplicateCalls: dupes,
    batchableCalls: batchable,
    startedAt: s.startedAt,
    calls,
  };
}

function notify(s: RegistryState): void {
  for (const l of s.listeners) {
    l();
  }
}

export interface RecordHeliusCallArgs {
  readonly method: string;
  readonly paramsPreview: string;
  readonly paramsKey: string;
  readonly bytes: number;
  readonly callSite: string | null;
  readonly responseBody?: string | null;
}

/** Cap captured response body strings — same constant the API registry
 *  uses. Avoids unbounded heap growth in long-running dev sessions. */
const HELIUS_MAX_BODY_BYTES = 50_000;

function truncateHeliusBody(s: string | null | undefined): string | null {
  if (s === null || s === undefined) return null;
  if (s.length <= HELIUS_MAX_BODY_BYTES) return s;
  return `${s.slice(0, HELIUS_MAX_BODY_BYTES)}\n…[truncated, ${String(s.length - HELIUS_MAX_BODY_BYTES)}B more]`;
}

export function recordHeliusCall(args: RecordHeliusCallArgs): void {
  if (!IS_DEV) return;
  const s = ensureState();
  const now = Date.now();
  const cutoff = now - WINDOW_MS;

  // ── Duplicate detection (same method + same params) ──────────────────
  const dupKey = `${args.method}::${args.paramsKey}`;
  const dupExisting = s.recent.get(dupKey);
  let burstId: number | null = null;
  let burstSize = 1;
  if (dupExisting !== undefined) {
    const fresh = dupExisting.timestamps.filter((t): boolean => t >= cutoff);
    if (fresh.length > 0) {
      burstId = dupExisting.burstId;
      burstSize = fresh.length + 1;
      // Retro-update older members of this duplicate burst — match by
      // (method, paramsKey) so the original singleton call gets
      // reclassified.
      for (let i = s.buffer.length - 1; i >= 0; i -= 1) {
        const c = s.buffer[i];
        if (c === undefined) continue;
        if (c.timestamp < cutoff) break;
        if (c.method === args.method && c.paramsKey === args.paramsKey) {
          c.burstId = burstId;
          c.burstSize = burstSize;
        }
      }
    }
    fresh.push(now);
    s.recent.set(dupKey, { timestamps: fresh, burstId: dupExisting.burstId });
  } else {
    s.burstSeq += 1;
    s.recent.set(dupKey, { timestamps: [now], burstId: s.burstSeq });
  }

  // ── Batchable detection (same method, ANY params, batchable list) ────
  const suggestion = BATCHABLE_METHODS[args.method];
  let batchId: number | null = null;
  let batchSize = 1;
  let batchSuggestion: string | null = null;
  if (suggestion !== undefined) {
    const recentTimestamps = s.methodRecent.get(args.method) ?? [];
    const fresh = recentTimestamps.filter((t): boolean => t >= cutoff);
    if (fresh.length > 0) {
      batchSize = fresh.length + 1;
      batchSuggestion = suggestion;
      // Find existing batch id by walking back through buffer.
      for (let i = s.buffer.length - 1; i >= 0; i -= 1) {
        const c = s.buffer[i];
        if (c === undefined) continue;
        if (c.timestamp < cutoff) break;
        if (c.method === args.method && c.batchId !== null) {
          batchId = c.batchId;
          break;
        }
      }
      if (batchId === null) {
        s.batchSeq += 1;
        batchId = s.batchSeq;
      }
      // Retro-update every same-method call in the window.
      for (let i = s.buffer.length - 1; i >= 0; i -= 1) {
        const c = s.buffer[i];
        if (c === undefined) continue;
        if (c.timestamp < cutoff) break;
        if (c.method === args.method) {
          c.batchId = batchId;
          c.batchSize = batchSize;
          c.batchSuggestion = suggestion;
        }
      }
    }
    fresh.push(now);
    s.methodRecent.set(args.method, fresh);
  }

  // ── Append the new record ────────────────────────────────────────────
  s.idSeq += 1;
  s.totalCalls += 1;
  s.totalBytes += args.bytes;
  const record: MutableHeliusCall = {
    id: s.idSeq,
    timestamp: now,
    method: args.method,
    paramsPreview: args.paramsPreview,
    paramsKey: args.paramsKey,
    bytes: args.bytes,
    callSite: args.callSite,
    burstSize,
    burstId: burstSize > 1 ? burstId : null,
    batchSize,
    batchId: batchSize > 1 ? batchId : null,
    batchSuggestion,
    responseBody: truncateHeliusBody(args.responseBody),
  };
  s.buffer.push(record);
  if (s.buffer.length > MAX_BUFFER_SIZE) {
    s.buffer.shift();
  }

  // Periodic GC of the timestamp maps.
  if (s.idSeq % 50 === 0) {
    for (const [k, v] of s.recent) {
      const newest = v.timestamps[v.timestamps.length - 1];
      if (newest === undefined || newest < cutoff) {
        s.recent.delete(k);
      }
    }
    for (const [k, v] of s.methodRecent) {
      const newest = v[v.length - 1];
      if (newest === undefined || newest < cutoff) {
        s.methodRecent.delete(k);
      }
    }
  }

  s.cachedSnapshot = freezeSnapshotFrom(s);
  notify(s);
}

export function subscribeHeliusRegistry(listener: () => void): () => void {
  if (!IS_DEV) return NOOP_UNSUBSCRIBE;
  const s = ensureState();
  s.listeners.add(listener);
  return (): void => {
    s.listeners.delete(listener);
  };
}

export function getHeliusSnapshot(): HeliusSnapshot {
  if (!IS_DEV) return NOOP_SNAPSHOT;
  return ensureState().cachedSnapshot;
}

export function clearHeliusStats(): void {
  if (!IS_DEV) return;
  const s = ensureState();
  s.buffer = [];
  s.recent.clear();
  s.methodRecent.clear();
  s.totalCalls = 0;
  s.totalBytes = 0;
  s.startedAt = Date.now();
  s.cachedSnapshot = freezeSnapshotFrom(s);
  notify(s);
}

/**
 * Walk the current stack and return the first frame inside our own
 * `src/` tree, so the registry's per-call-site bucket points at the JS
 * caller (e.g. `queries/wallet-balance.ts:120`) rather than internal
 * `@solana/web3.js` plumbing. Returns `null` if no app frame is found.
 */
export function captureCallSite(): string | null {
  if (!IS_DEV) return null;
  const err = new Error();
  const stack = err.stack;
  if (typeof stack !== 'string') return null;
  const lines = stack.split('\n');
  for (const raw of lines) {
    const line = raw.trim();
    if (line.length === 0) continue;
    if (line.includes('helius-registry')) continue;
    if (line.includes('helius-instrument')) continue;
    if (line.includes('@solana/web3.js')) continue;
    if (line.includes('node_modules')) continue;
    if (line.includes('/web3.js/')) continue;

    const match = /\/src\/([^):\s]+:\d+(?::\d+)?)/.exec(line);
    if (match !== null && match[1] !== undefined) {
      return match[1].replace(/:\d+$/, '');
    }
  }
  return null;
}

export function formatHeliusSnapshot(snapshot: HeliusSnapshot, now: number): string {
  const lines: string[] = [];
  const elapsedS = Math.max(1, Math.floor((now - snapshot.startedAt) / 1000));
  const ratePerMin = ((snapshot.totalCalls / elapsedS) * 60).toFixed(1);
  lines.push(`# Helius RPC ledger`);
  lines.push(`captured: ${new Date(now).toISOString()}`);
  lines.push(
    `total: ${String(snapshot.totalCalls)} calls / ${formatBytes(snapshot.totalBytes)}` +
      ` over ${String(elapsedS)}s (~${ratePerMin}/min)` +
      ` — duplicates: ${String(snapshot.duplicateCalls)}` +
      ` · batchable: ${String(snapshot.batchableCalls)}`,
  );
  lines.push('');
  lines.push('## Calls (newest first)');
  for (let i = snapshot.calls.length - 1; i >= 0; i -= 1) {
    const c = snapshot.calls[i];
    if (c === undefined) continue;
    const flags: string[] = [];
    if (c.burstSize > 1) flags.push(`⚠ DUPE×${String(c.burstSize)}`);
    if (c.batchSize > 1 && c.batchSuggestion !== null) {
      flags.push(`◆ BATCHABLE×${String(c.batchSize)} → ${c.batchSuggestion}`);
    }
    const flagStr = flags.length > 0 ? ` ${flags.join(' ')}` : '';
    const site = c.callSite !== null ? ` @ ${c.callSite}` : '';
    lines.push(
      `- \`${formatTime(c.timestamp)}\` **${c.method}**(${c.paramsPreview})` +
        ` ${formatBytes(c.bytes)}${site}${flagStr}`,
    );
  }
  return lines.join('\n');
}

function formatTime(ts: number): string {
  const d = new Date(ts);
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  const ss = String(d.getSeconds()).padStart(2, '0');
  const ms = String(d.getMilliseconds()).padStart(3, '0');
  return `${hh}:${mm}:${ss}.${ms}`;
}

function formatBytes(n: number): string {
  if (n < 1024) return `${String(n)}B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)}KB`;
  return `${(n / (1024 * 1024)).toFixed(2)}MB`;
}

/** Test-only — wipes registry state. Not exported from the public barrel. */
export function __resetHeliusRegistryForTests(): void {
  _state = null;
  const g = globalThis as typeof globalThis & HeliusGlobals;
  delete g.__morfiHeliusLedger;
}
